'use client'

import { useSyncExternalStore } from 'react'

// iubenda cookie-policy purpose ids (siteId 2503784). Purpose 1 (Necessary) is never gated.
export const THIRD_PARTY_PURPOSE = 3
export const MEASUREMENT_PURPOSE = 4
export const MARKETING_PURPOSE = 5

// `undefined` = the banner has not been answered yet (iubenda not ready / still open)
type ConsentValue = boolean | undefined

const listeners = new Set<() => void>()

const notify = () => {
    for (const listener of listeners) listener()
}

const readPurpose = (purposeId: number): ConsentValue => {
    if (typeof window === 'undefined') return undefined

    const api = window._iub?.cs?.api

    if (!api?.isPreferenceExpressed || !api?.getPurposesState) return undefined

    // iubenda's API can throw while its locale core is still initialising, and this runs inside
    // useSyncExternalStore's snapshot (during render) — an uncaught throw would crash the tree.
    try {
        // No preference expressed yet → banner still open → let the gate show its facade.
        if (!api.isPreferenceExpressed()) return undefined

        // Dedicated per-purpose state map, keyed by iubenda purpose id (1..5).
        return !!api.getPurposesState()[purposeId]
    } catch {
        return undefined
    }
}

// Live updates: iubenda's banner callback (see cookie-banner.tsx) calls `window.__onConsentChange`
// on every preference expression/change. We point it at `notify` so mounted gates re-read the new
// consent state — no monkey-patching of GTM's dataLayer.push required.
const registerConsentBridge = () => {
    if (typeof window === 'undefined') return

    window.__onConsentChange = notify
}

const subscribe = (onChange: () => void): (() => void) => {
    listeners.add(onChange)
    registerConsentBridge()

    return () => {
        listeners.delete(onChange)
    }
}

export const usePurposeConsent = (purposeId: number): ConsentValue =>
    useSyncExternalStore(
        subscribe,
        () => readPurpose(purposeId),
        () => undefined
    )
